import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Typography,
  Button,
  Card,
  Tooltip,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getAnomalyIcon } from 'farmx-web-ui';
import { ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { hooks, selectors, actions } from 'farmx-redux-core';
import { recommendationApi } from 'farmx-api';
import {
  getTextForIrrigationState,
  getStateForIrrigationState,
  getActionText,
} from './recommendation';
import { StatusTag } from '../components/StatusTag';
import { useTracking } from '../../../helper/mixpanel';
import HelpButton from '../components/HelpButton';
import SoilStateLegendModal from './SoilStateLegendModal';
import { getStateForAnomalySeverity } from '../../../helper/anomalyHelper';
import {
  getHumanReadableAnomalyType,
  getRecommendationIdentifier,
  checkIsActionNeeded,
  parseDuration,
  parseWater,
} from '../../../helper/common';
import {
  formatIrrigationDepthUnit,
  formatIrrigationDepthValue,
  formatOneDecimalValue,
  formatCropName,
} from './recommendationFormat';
import { renderTemplateString } from '../../../helper/templateString';
import {
  IRRIGATION_RECOMMENDATION_FORECAST_RISK_STATES,
  IRRIGATION_RECOMMENDATION_RISK_STATES,
  IRRIGATION_RECOMMENDATION_STATES,
  RECOMMENDATION_PRIORITY_LEVELS,
} from '../../../constants';

const { useBlockNames } = hooks;

const { selectNewBlockById, selectUserLengthFormat } = selectors;

const {
  loadRecommendationForBlock,
} = actions;

export function RecommendationListItem({
  selectedTab, recommendation, loadInitialTab, anomalies,
}) {
  const history = useHistory();
  const location = useLocation();
  const { push } = history;
  const { t } = useTranslation();
  const tracking = useTracking();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const getUserUnits = hooks.useUnits();
  const blockName = useBlockNames([Number(recommendation?.block)]);
  const block = useSelector(
    (state) => selectNewBlockById(state, Number(recommendation?.block))
  ) || {};
  const userUnitLength = useSelector((state) => selectUserLengthFormat(state));
  const ranchId = block?.ranch;
  const cropName = block?.crop;
  const toUnitByLength = userUnitLength === 'feet' ? 'inches' : 'millimeters';
  const staleReason = recommendation?.data?.staleReason;
  const isStale = Boolean(staleReason);
  const irrigationDurationSource = recommendation?.data?.recommendedIrrigationDuration;
  const durationValue = irrigationDurationSource
    ? parseDuration(irrigationDurationSource, t)
    : null;
  const totalWaterValue = recommendation?.totalWater !== null
    && recommendation?.totalWater !== undefined
    ? parseWater(
      recommendation.totalWater,
      recommendation?.units?.totalWater || 'millimeters',
      getUserUnits,
      toUnitByLength,
      t,
    )
    : null;
  const depthUnit = getUserUnits(
    0,
    'inches',
    undefined,
    { decimalPlaces: 2 },
    toUnitByLength,
  )?.label || 'inches';
  const deficitValue = recommendation?.deficit !== null
    && recommendation?.deficit !== undefined
    ? parseWater(
      recommendation.deficit,
      recommendation?.units?.deficit || 'millimeters',
      getUserUnits,
      toUnitByLength,
      t,
    )
    : null;
  const etcImpactValue = recommendation?.etcForecast !== null
    && recommendation?.etcForecast !== undefined
    ? parseWater(
      recommendation.etcForecast,
      recommendation?.units?.etcForecast || 'millimeters',
      getUserUnits,
      toUnitByLength,
      t,
    )
    : null;

  const translatedDescription = renderTemplateString(
    t(recommendation?.description),
    {
      irrigation_duration: Math.round(
        moment.duration(recommendation.irrigationDuration).asHours()
      ),
      date_wilting_point: moment(recommendation?.data?.dateWiltingPoint).format(
        'D MMMM'
      ),
    }
  );

  function getDepthUnitLabel() {
    return formatIrrigationDepthUnit(depthUnit, t);
  }

  function getUrgencyReason() {
    return recommendation?.urgencyReason
      || recommendation?.recommendationPriorityReason
      || recommendation?.priorityReason
      || recommendation?.data?.urgencyReason
      || recommendation?.data?.recommendationPriorityReason
      || recommendation?.data?.priorityReason
      || null;
  }

  function getPriorityLevel() {
    if (
      recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.VERY_DRY
      || recommendation?.forecastState === IRRIGATION_RECOMMENDATION_STATES.VERY_DRY
    ) {
      return RECOMMENDATION_PRIORITY_LEVELS.URGENT;
    }
    if (
      recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.DRY
      || recommendation?.forecastState === IRRIGATION_RECOMMENDATION_STATES.DRY
    ) {
      return RECOMMENDATION_PRIORITY_LEVELS.HIGH;
    }
    if (recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.DRYING) {
      return RECOMMENDATION_PRIORITY_LEVELS.MONITOR;
    }
    if (recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.OVER_IRRIGATED) {
      return RECOMMENDATION_PRIORITY_LEVELS.REDUCE;
    }
    return RECOMMENDATION_PRIORITY_LEVELS.STABLE;
  }

  function getPriorityText() {
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.URGENT) {
      return t('Urgent');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.HIGH) {
      return t('More Urgent');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.MONITOR) {
      return t('Monitor');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.REDUCE) {
      return t('Reduce Water');
    }
    return t('Stable');
  }

  function getPriorityTooltipText() {
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.URGENT) {
      return t('Urgent means this block needs immediate attention based on current or forecast soil moisture.');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.HIGH) {
      return t('More Urgent means water need is increasing and should be reviewed soon.');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.MONITOR) {
      return t('Monitor means the block is trending drier and should be watched.');
    }
    if (getPriorityLevel() === RECOMMENDATION_PRIORITY_LEVELS.REDUCE) {
      return t('Reduce Water means the block may be receiving too much irrigation.');
    }
    return t('Stable means no immediate irrigation issue is detected.');
  }

  function getUpdatedText() {
    if (!recommendation?.dateCreated) return null;
    return t('Updated {{time}}', {
      time: moment(recommendation.dateCreated).fromNow(),
    });
  }

  function isRiskState(state) {
    return IRRIGATION_RECOMMENDATION_RISK_STATES.includes(state);
  }

  function isForecastRiskState(state) {
    return IRRIGATION_RECOMMENDATION_FORECAST_RISK_STATES.includes(state);
  }

  function isBackendDescriptionSafe() {
    if (!translatedDescription) {
      return false;
    }
    if (recommendation?.action && recommendation.action !== 'irrigate') {
      return true;
    }
    const lowerDescription = translatedDescription.toLowerCase();
    const hasDirectIrrigationInstruction = lowerDescription.includes('irrigate for')
      || lowerDescription.includes('plan irrigation for');
    if (
      [
        IRRIGATION_RECOMMENDATION_STATES.WELL_IRRIGATED,
        IRRIGATION_RECOMMENDATION_STATES.OVER_IRRIGATED,
      ].includes(recommendation?.state)
      && hasDirectIrrigationInstruction
    ) {
      return false;
    }
    return true;
  }

  function getCompactRecommendationDescription() {
    if (isStale) {
      return t('Outdated recommendation. Open details before acting.');
    }
    if (recommendation?.action && recommendation.action !== 'irrigate' && isBackendDescriptionSafe()) {
      return translatedDescription;
    }
    if (isRiskState(recommendation?.state)) {
      return isBackendDescriptionSafe()
        ? translatedDescription
        : t('Irrigation is recommended based on current soil moisture and forecast demand.');
    }
    if (
      recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.WELL_IRRIGATED
      && isForecastRiskState(recommendation?.forecastState)
    ) {
      return t('Currently well irrigated, but forecast shows water need increasing over the next 5 days.');
    }
    if (
      recommendation?.state === IRRIGATION_RECOMMENDATION_STATES.OVER_IRRIGATED
      && isForecastRiskState(recommendation?.forecastState)
    ) {
      return t('Currently over-irrigated, but forecast demand may reduce the surplus over the next 5 days.');
    }
    if (isBackendDescriptionSafe()) {
      return translatedDescription;
    }
    return t('No immediate irrigation action needed.');
  }

  function getActionButtonText() {
    if (isStale) {
      return t('Outdated recommendation');
    }
    if (recommendation?.action === 'no_action') {
      return t('Irrigate');
    }
    if (checkIsActionNeeded(recommendation)) {
      return t(getActionText(recommendation.action));
    }
    return t('Action Taken');
  }

  function shouldShowDescriptionToggle(description) {
    return description?.length > 85;
  }

  const onDescriptionToggleClick = useCallback((event) => {
    event.stopPropagation();
    setIsDescriptionExpanded((isExpanded) => !isExpanded);
  }, []);

  function getFourthMetric() {
    if (recommendation?.action && recommendation.action !== 'irrigate') {
      return {
        value: t(getActionText(recommendation.action)),
        label: t('Action'),
      };
    }
    return {
      value: durationValue !== null ? `${formatOneDecimalValue(durationValue)} ${t('hr')}` : t('N/A'),
      label: t('Duration'),
    };
  }

  function renderConditionFlow() {
    return (
      <div className="recommendation-condition-flow">
        <StatusTag
          text={getTextForIrrigationState(recommendation.state)}
          state={getStateForIrrigationState(recommendation.state)}
          width="auto"
          tagStyle={{
            fontSize: '14px',
            minHeight: '32px',
            padding: '5px 12px',
            margin: 0,
          }}
        />
        <div className="recommendation-condition-flow-connector">
          <span className="recommendation-condition-line" />
          <Typography.Text className="recommendation-condition-days">
            {t('5 days')}
          </Typography.Text>
          <span className="recommendation-condition-line" />
          <RightOutlined className="recommendation-condition-arrow" />
        </div>
        <StatusTag
          text={getTextForIrrigationState(recommendation.forecastState)}
          state={getStateForIrrigationState(recommendation.forecastState)}
          width="auto"
          tagStyle={{
            fontSize: '14px',
            minHeight: '32px',
            padding: '5px 12px',
            margin: 0,
          }}
        />
      </div>
    );
  }

  const onRefreshClick = useCallback(async (event) => {
    event.stopPropagation();
    setIsRefreshing(true);
    try {
      await recommendationApi.refreshIrrigationRecommendation({
        blockId: recommendation.block,
      });
      await dispatch(loadRecommendationForBlock(recommendation.block));
    } finally {
      setIsRefreshing(false);
    }
  }, [dispatch, recommendation.block]);

  const onDetailsClick = useCallback(() => {
    push({
      pathname: `/recommendation/detail/${getRecommendationIdentifier(recommendation)}`,
      state: { selectedTab },
    });
    tracking.track('Clicked recommendation details', recommendation);
  }, [selectedTab, push, recommendation, tracking]);

  const openModal = useCallback((e) => {
    e.stopPropagation();
    setShowModal(true);
  }, []);

  const closeModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const onActionButtonClick = useCallback((event) => {
    event.stopPropagation();
    push({
      pathname: `/recommendation/action/${recommendation.block}`,
      state: {
        from: location.pathname,
        selectedTab,
      },
    });
    tracking.track('Clicked recommendation action', recommendation);
  }, [selectedTab, location.pathname, push, recommendation, tracking]);

  const onAnomalyButtonClick = useCallback((event, anomalyId) => {
    event.stopPropagation();
    const anomalyData = anomalies.filter((d) => (d?.anomaly?.properties?.id === anomalyId));

    push({
      pathname: `/recommendation/anomaly/detail/${getRecommendationIdentifier(recommendation)}`,
      state: { selectedTab, anomalyId },
    });
    tracking.track('Clicked anomaly details', anomalyData);
  }, [anomalies, push, recommendation, selectedTab, tracking]);

  const onAnomalyActionButtonClick = useCallback((event, anomalyId) => {
    event.stopPropagation();
    const anomalyData = anomalies.filter((d) => (d?.anomaly?.properties?.id === anomalyId));

    push({
      pathname: `/recommendation/anomaly/investigate/${recommendation.block}`,
      state: { from: location.pathname, selectedTab, anomalyId },
    });
    tracking.track('Clicked anomaly action', anomalyData);
  }, [anomalies, location.pathname, push, recommendation.block, selectedTab, tracking]);

  const onGroundMissionButtonClick = useCallback((event) => {
    event.stopPropagation();
    push({
      pathname: `/recommendation/ground_mission/detail/${getRecommendationIdentifier(recommendation)}`,
      state: { selectedTab, drone: recommendation },
    });
    tracking.track('Clicked drone details', recommendation);
  }, [push, recommendation, selectedTab, tracking]);

  const { selectedTab: initialTab } = location.state ?? {};

  const onGroundMissionActionButtonClick = useCallback((event) => {
    event.stopPropagation();
    push({
      pathname: `/recommendation/ground_mission/action/${recommendation.block}`,
      state: {
        from: location.pathname,
        selectedTab,
        ranchId,
      },
    });
    tracking.track('Clicked recommendation action', recommendation);
  }, [selectedTab, location.pathname, push, recommendation, tracking, ranchId]);

  useEffect(() => {
    loadInitialTab(initialTab);
  }, [initialTab, loadInitialTab]);

  function renderAnomalyRecommendationListItem() {
    if (recommendation.type !== 'imagery_anomaly') return null;

    return (
      <Card
        className="recommendation-list recommendation-list-anomaly"
        key={recommendation.id}
        title={recommendation.blockName || blockName[Number(recommendation.block)]}
        extra={(
          <div className="flex-center column-gap-10 recommendation-state-row">
            {getUpdatedText() ? (
              <Typography.Text className="recommendation-header-updated-text">
                {getUpdatedText()}
              </Typography.Text>
            ) : null}
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={isRefreshing} />}
              onClick={onRefreshClick}
              className="recommendation-card-refresh-button"
            />
            <HelpButton onClick={openModal} className="recommendation-card-help-button" />
          </div>
        )}
        actions={[
          <div className="recommendation-list-item-footer">
            {recommendation.action !== 'no_action' ? (
              <div className="action-container-primary">
                <Button
                  size="large"
                  block
                  type="primary"
                  onClick={onAnomalyActionButtonClick}
                >
                  {t(getActionText(recommendation?.action))}
                </Button>
              </div>
            ) : (
              <div className="action-container-primary">
                <Button
                  size="large"
                  block
                  onClick={(event) => onAnomalyButtonClick(
                    event,
                    recommendation?.imageryAnomaly
                  )}
                >
                  {t('View Details')}
                </Button>
              </div>
            )}
          </div>]}
        bordered
        onClick={(e) => onAnomalyButtonClick(e, recommendation?.imageryAnomaly)}
      >
        <div className="recommendation-list-item-content recommendation-anomaly-content">
          <div className="recommendation-card-meta-row">
            <StatusTag
              text={getHumanReadableAnomalyType(recommendation?.anomalyType)}
              state={getStateForAnomalySeverity(recommendation?.anomalySeverity)}
              icon={getAnomalyIcon(recommendation?.anomalyType)}
            />
          </div>
          <Typography.Text
            className="recommendation-description"
          >
            {translatedDescription}
          </Typography.Text>
          {recommendation.action !== 'no_action' ? (
            <div className="recommendation-card-footer-row recommendation-anomaly-footer-row">
              <span />
              <Typography.Text className="recommendation-more-cta">
                <span className="ant-btn-link">{t('More details')}</span>
              </Typography.Text>
            </div>
          ) : null}
        </div>
      </Card>
    );
  }

  function renderIrrigationRecommendationListItem() {
    const recommendationTitle = recommendation.blockName || blockName[Number(recommendation.block)];
    const fourthMetric = getFourthMetric();
    const urgencyReason = getUrgencyReason();
    const compactDescription = getCompactRecommendationDescription();
    const cardTitle = (
      <span className="recommendation-card-title">
        {isStale ? (
          <Tooltip title={t('This recommendation is outdated and should not be acted on without checking details.')}>
            <span className="recommendation-stale-title-badge">
              {t('Outdated')}
            </span>
          </Tooltip>
        ) : null}
        <span>{recommendationTitle}</span>
      </span>
    );

    return (
      <Card
        className={`recommendation-list ${isStale ? 'recommendation-list-stale' : ''}`}
        key={recommendation.id}
        title={cardTitle}
        extra={(
          <div className="flex-center column-gap-10 recommendation-state-row">
            {getUpdatedText() ? (
              <Typography.Text className="recommendation-header-updated-text">
                {getUpdatedText()}
              </Typography.Text>
            ) : null}
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={isRefreshing} />}
              onClick={onRefreshClick}
              className="recommendation-card-refresh-button"
            />
            <HelpButton onClick={openModal} className="recommendation-card-help-button" />
          </div>
        )}
        actions={[
          <div className="recommendation-list-item-footer">
            {recommendation.action !== 'no_action' ? (
              <div className="action-container-primary">
                <Button
                  size="large"
                  block
                  type="primary"
                  onClick={onActionButtonClick}
                  disabled={isStale || !checkIsActionNeeded(recommendation)}
                >
                  {getActionButtonText()}
                </Button>
              </div>
            ) : (
              <div className="action-container-primary">
                <Tooltip title={t('No irrigation action is needed for this recommendation right now.')}>
                  <span className="recommendation-disabled-action-wrapper">
                    <Button
                      size="large"
                      block
                      type="primary"
                      disabled
                    >
                      {getActionButtonText()}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            )}
          </div>]}
        bordered
        onClick={onDetailsClick}
      >
        <div className="recommendation-list-item-content">
          <div className="recommendation-card-meta-row">
            {cropName ? (
              <Tooltip title={t('Crop configured for this block.')}>
                <Typography.Text className="recommendation-crop-chip">
                  {formatCropName(cropName)}
                </Typography.Text>
              </Tooltip>
            ) : null}
            <Tooltip title={getPriorityTooltipText()}>
              <Typography.Text className={`recommendation-priority-chip recommendation-priority-chip-${getPriorityLevel()}`}>
                {getPriorityText()}
              </Typography.Text>
            </Tooltip>
          </div>
          {renderConditionFlow()}
          <div className="recommendation-card-copy">
            <div className="recommendation-card-description-row">
              <Typography.Text
                className={`recommendation-card-description ${isDescriptionExpanded
                  ? 'recommendation-card-description-expanded'
                  : ''}`}
              >
                {compactDescription}
              </Typography.Text>
              {shouldShowDescriptionToggle(compactDescription) ? (
                <Button
                  type="link"
                  size="small"
                  className="recommendation-description-toggle"
                  onClick={onDescriptionToggleClick}
                >
                  {isDescriptionExpanded ? t('less') : t('more')}
                </Button>
              ) : null}
            </div>
            {urgencyReason ? (
              <Typography.Text className="recommendation-urgency-reason">
                {urgencyReason}
              </Typography.Text>
            ) : null}
          </div>
          <div className="recommendation-metric-strip">
            <div className="recommendation-metric-cell">
              <Typography.Text className="recommendation-metric-value">
                {totalWaterValue !== null ? `${formatIrrigationDepthValue(totalWaterValue)} ${getDepthUnitLabel()}` : t('N/A')}
              </Typography.Text>
              <Typography.Text className="recommendation-metric-label">
                {t('Water Needed')}
              </Typography.Text>
            </div>
            <div className="recommendation-metric-cell">
              <Typography.Text className="recommendation-metric-value">
                {deficitValue !== null ? `${formatIrrigationDepthValue(deficitValue)} ${getDepthUnitLabel()}` : t('N/A')}
              </Typography.Text>
              <Typography.Text className="recommendation-metric-label">
                {t('Deficit')}
              </Typography.Text>
            </div>
            <div className="recommendation-metric-cell">
              <Typography.Text className="recommendation-metric-value">
                {etcImpactValue !== null ? `+${formatIrrigationDepthValue(etcImpactValue)} ${getDepthUnitLabel()}` : t('N/A')}
              </Typography.Text>
              <Typography.Text className="recommendation-metric-label">
                {t('ETc Impact')}
              </Typography.Text>
            </div>
            <div className="recommendation-metric-cell">
              <Typography.Text className="recommendation-metric-value">
                {fourthMetric.value}
              </Typography.Text>
              <Typography.Text className="recommendation-metric-label">
                {fourthMetric.label}
              </Typography.Text>
            </div>
          </div>
          <div className="recommendation-card-footer-row">
            <span />
            <Typography.Text className="recommendation-more-cta">
              <span className="ant-btn-link">{t('More details')}</span>
            </Typography.Text>
          </div>
        </div>
      </Card>
    );
  }

  function renderGroundMissionRecommendationListItem() {
    return (
      <Card
        className="recommendation-list"
        key={recommendation.id}
        title={recommendation.blockName || blockName[Number(recommendation.block)]}
        extra={(
          <div className="flex-center column-gap-10">
            <StatusTag
              text={getHumanReadableAnomalyType(recommendation.state)}
              state={getStateForAnomalySeverity(recommendation.state)}
              icon={getAnomalyIcon(recommendation.type)}
            />
          </div>
        )}
        actions={[
          <div className="recommendation-list-item-footer">
            {recommendation.action !== 'no_action' ? (
              <div className="action-container-primary">
                <Button
                  size="large"
                  block
                  type="primary"
                  onClick={onGroundMissionActionButtonClick}
                >
                  {t(getActionText(recommendation.action))}
                </Button>
              </div>
            ) : null}
          </div>]}
        bordered
        onClick={(e) => onGroundMissionButtonClick(e, recommendation.id)}
      >
        <div className="recommendation-list-item-content">
          <Typography.Text
            className="recommendation-description"
          >
            {translatedDescription}
          </Typography.Text>
          <Typography.Text
            className="recommendation-description"
          >
            <span className="ant-btn-link">{` ${t('More...')}`}</span>
          </Typography.Text>
        </div>
      </Card>
    );
  }

  return (
    <>
      {recommendation.type === 'irrigation' && renderIrrigationRecommendationListItem()}
      {recommendation.type === 'imagery_anomaly' && renderAnomalyRecommendationListItem()}
      {(!recommendation.type || recommendation.type === 'ground_mission')
        && renderGroundMissionRecommendationListItem()}
      <SoilStateLegendModal
        visible={showModal}
        onCancel={closeModal}
      />
    </>
  );
}

RecommendationListItem.defaultProps = {
  selectedTab: 'new',
  loadInitialTab: () => { },
  anomalies: [],
};

RecommendationListItem.propTypes = {
  recommendation: PropTypes.shape({
    action: PropTypes.string,
    anomalySeverity: PropTypes.string,
    anomalyType: PropTypes.string,
    block: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    blockName: PropTypes.string,
    data: PropTypes.shape({
      dateWiltingPoint: PropTypes.string,
      priorityReason: PropTypes.string,
      recommendationPriorityReason: PropTypes.string,
      staleReason: PropTypes.string,
      urgencyReason: PropTypes.string,
    }),
    dateCreated: PropTypes.string,
    dateIrrigationStart: PropTypes.string,
    deficit: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    etcForecast: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    forecastState: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    imageryAnomaly: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    irrigationDuration: PropTypes.string,
    priorityReason: PropTypes.string,
    recommendationPriorityReason: PropTypes.string,
    state: PropTypes.string,
    staleReason: PropTypes.string,
    totalWater: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    type: PropTypes.string,
    urgencyReason: PropTypes.string,
    units: PropTypes.shape({
      deficit: PropTypes.string,
      etcForecast: PropTypes.string,
      totalWater: PropTypes.string,
    }),
  }).isRequired,
  selectedTab: PropTypes.string,
  loadInitialTab: PropTypes.func,
  anomalies: PropTypes.arrayOf(PropTypes.shape({
    anomaly: PropTypes.shape({
      properties: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      }),
    }),
  })),
};
